import React, { useCallback, useEffect, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { Box, Pressable, Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';

import { LogUserErrorSeverity, logUserErrorMessage } from 'hooks/log-user-error-message';
import useEffectOnce from 'hooks/use-effect-once';
import { HttpErrorCodes } from 'remote/constants';
import { useAuthContext } from 'state/auth';
import {
  getStoredOtpCredentials,
  storeOtpCredentials,
} from 'state/auth/hooks/use-account-authentication';
import { ModalAuthScreen } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { TraceName, stopTrace } from 'utils/performance/trace';
import { routes } from 'utils/routing';

import { OtpForm } from './otp-form';
import {
  CodeSentText,
  CodeText,
  ErrorMessage,
  NotReceived,
  PressableTextWrapper,
  SentEmailText,
  TransformedHeadline,
} from './otp.styled';
import { useResendOtp } from './use-resend-otp.hook';
import { useSendNewOtp } from './use-send-new-otp.hook';

interface GraphQLError {
  graphQLErrors?: Array<{
    extensions?: {
      statusCode?: number;
    };
  }>;
}

export const ConfirmOtp = () => {
  const {
    isAuthenticated,
    originLocation,
    setOriginLocation,
    validateLoginOtp,
    setOtpAuthError,
    modalAuthIsOpen,
    setModalAuthState,
    onSignInSuccess,
  } = useAuthContext();

  const client = useApolloClient();

  const { navigate } = useRouter();
  const { email, sessionId } = getStoredOtpCredentials() || {};
  const [isValidatingOtp, setIsValidatingOtp] = useState(false);
  const [showSendNewOtpForm, setShowSendNewOtpForm] = useState(false);

  const [otpValidationError, setOtpValidationError] = useState<string | null>(null);
  const [resendOrSendNewOtpError, setResendOrSendNewOtpError] = useState<string | null>(null);
  const [showHasResentOtpMessage, setShowHasSentResentOtpMessage] = useState(false);
  const [showHasSentNewOtpMessage, setShowHasSentNewOtpMessage] = useState(false);

  const hasEmail = Boolean(email?.length);

  const { sendNewOtp, isLoading: isRequestingNewCode } = useSendNewOtp({
    setOtpValidationError,
    setOtpAuthError,
    setResendOrSendNewOtpError,
    setShowHasSentNewOtpMessage,
    setShowSendNewOtpForm,
  });

  const { resendOtp, isLoading: isResendingOtp } = useResendOtp({
    setOtpValidationError,
    setResendOrSendNewOtpError,
    setShowHasSentResentOtpMessage,
    setShowSendNewOtpForm,
  });

  // Either if we have a login, or once we validate OTP and get an update for the user being loaded
  // we can then handle the navigate.
  useEffect(() => {
    // If the confirm OTP is rendered in the ModalAuth component, we don't want to navigate away from the modal.
    if (modalAuthIsOpen) {
      return;
    }

    if (isAuthenticated) {
      const path = originLocation || routes.base;
      setOriginLocation(null);
      navigate({
        pathname: path,
        // This allows us to notify the screen reader to announce a successful sign in only when signing in via OTP.
        params: {
          triggerSignInAccessibility: 1,
        },
      });
    }
  }, [isAuthenticated, originLocation, modalAuthIsOpen, navigate, setOriginLocation]);

  const handleValidateLogin = useCallback(
    async (otpCode: string) => {
      setIsValidatingOtp(true);
      setShowHasSentNewOtpMessage(false);
      setOtpValidationError(null);
      setOtpAuthError(null);
      setResendOrSendNewOtpError(null);

      try {
        await validateLoginOtp({ otpCode });
        // refetch offers when user navigates to /offers page
        client.reFetchObservableQueries();
        onSignInSuccess();
        stopTrace(TraceName.OTP_FLOW);
      } catch (error) {
        let errorMessage;
        setIsValidatingOtp(false);

        if (!sessionId) {
          errorMessage = logUserErrorMessage({
            message: t('differentDeviceError'),
            severity: LogUserErrorSeverity.Recoverable,
          });

          setShowSendNewOtpForm(true);
          setOtpValidationError(errorMessage);
          setOtpAuthError(errorMessage);
          throw error;
        }

        const graphQLError = error as GraphQLError;
        switch (graphQLError?.graphQLErrors?.[0]?.extensions?.statusCode) {
          case HttpErrorCodes.InternalServerError:
            errorMessage = logUserErrorMessage({
              message: t('looksLikeWereExperiencingIssues'),
              severity: LogUserErrorSeverity.Irrecoverable,
            });
            setShowSendNewOtpForm(false);
            break;
          case HttpErrorCodes.TooManyRequests:
            errorMessage = logUserErrorMessage({
              message: t('maxAttemptsReachedRequestNewCode'),
              severity: LogUserErrorSeverity.Recoverable,
            });
            setShowSendNewOtpForm(true);
            break;
          case HttpErrorCodes.Unauthorized:
          default:
            errorMessage = logUserErrorMessage({
              message: t('invalidOtpProvided'),
              severity: LogUserErrorSeverity.Recoverable,
            });
            setShowSendNewOtpForm(true);
            break;
        }
        setOtpValidationError(errorMessage);
        setOtpAuthError(errorMessage);
        throw error;
      }
    },
    [setOtpAuthError, validateLoginOtp, client, onSignInSuccess, sessionId]
  );

  useEffectOnce(() => {
    if (email) {
      storeOtpCredentials({ sessionId, email });
    } else {
      setModalAuthState({
        screen: ModalAuthScreen.SIGN_IN,
      });
    }
  });

  return (
    <Box safeAreaBottom>
      <TransformedHeadline>{t('authVerifyWithCode')}</TransformedHeadline>
      {hasEmail && (
        <SentEmailText aria-label={t('visuallyHiddenSignInCodeSentToEmail')}>
          {t('authEmailSentEmail')}
          &nbsp;
          <Text testID="otp-email" data-private>
            {email}
          </Text>
        </SentEmailText>
      )}

      <OtpForm
        errorMessage={otpValidationError}
        loading={isValidatingOtp}
        onSubmit={handleValidateLogin}
      />

      {showSendNewOtpForm && (
        <Pressable
          alignSelf="center"
          disabled={isRequestingNewCode || isValidatingOtp}
          onPress={sendNewOtp}
          testID="otp-send-new-form"
          mt="$4"
        >
          <PressableTextWrapper>{t('sendNewCode')}</PressableTextWrapper>
        </Pressable>
      )}

      {showHasSentNewOtpMessage && hasEmail && (
        <CodeSentText aria-live="polite">{t('newCodeSent')}</CodeSentText>
      )}

      {/** resend OTP & send new OTP forms are never visible at the same time, they alternate */}
      {!showSendNewOtpForm && (
        <>
          <NotReceived>
            <CodeText>{t('didNotReceiveCodeHeading')}</CodeText>
            <Pressable
              disabled={isValidatingOtp || isResendingOtp}
              onPress={resendOtp}
              testID="otp-resend-form"
            >
              <PressableTextWrapper>{t('resendCode')}</PressableTextWrapper>
            </Pressable>
          </NotReceived>
          {!showHasSentNewOtpMessage && showHasResentOtpMessage && hasEmail && (
            <CodeSentText testID="otp-resend-success" aria-live="polite">
              {t('signInCodeSentToEmailAgain')}
            </CodeSentText>
          )}
        </>
      )}

      {resendOrSendNewOtpError && (
        <ErrorMessage testID="otp-resend-or-send-new-error">{resendOrSendNewOtpError}</ErrorMessage>
      )}
    </Box>
  );
};

export default ConfirmOtp;
